<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <!-- Navigation -->
    <nav class="bg-white shadow-sm sticky top-0 z-50">
      <div class="container mx-auto px-4">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center space-x-8">
            <router-link :to="{ name: getRouteName('homepage') }" class="flex items-center space-x-2">
              <svg
                class="h-8 w-8 text-[#233862]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3 18c.8-4.8 4-8 10-8m0 0c6 0 9.2 3.2 10 8M13 10V4l-4 2 4-2" />
                <path d="M3 18h18" />
              </svg>
              <span class="text-[#233862] text-xl font-bold tracking-wide">AKTOPLOIKA</span>
            </router-link>
          </div>
          <div class="hidden md:flex items-center space-x-6">
            <router-link
              v-for="item in navItems"
              :key="item.routeName"
              :to="{ name: item.routeName }"
              class="text-[#233862] hover:text-[#3D5A98] text-sm font-medium transition-colors"
              :class="{ 'text-[#3D5A98]': isCurrentRoute(item.routeName) }"
            >
              {{ item.name }}
            </router-link>
            <div class="border-l border-gray-200 h-6 mx-2"></div>
            <button
              @click="switchLanguage"
              class="text-[#233862] hover:text-[#3D5A98] text-sm font-medium transition-colors"
            >
              {{ currentLanguage === 'en' ? 'Ελληνικά' : 'English' }}
            </button>
            <a
              href="https://www.ferryhopper.com/el/?aff_uid=webee"
              target="_blank"
              class="bg-[#E65100] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#F57C00] transition-colors"
            >
              {{ currentLanguage === 'en' ? 'Book Now' : 'Κράτηση' }}
            </a>
          </div>
          <!-- Mobile menu button -->
          <div class="md:hidden">
            <button @click="toggleMobileMenu" class="text-[#233862]" aria-label="Toggle menu">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  v-if="!isMobileMenuOpen"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <!-- Mobile menu -->
      <div v-show="isMobileMenuOpen" class="md:hidden bg-white border-t border-gray-100">
        <div class="px-4 pt-2 pb-3 space-y-1">
          <router-link
            v-for="item in navItems"
            :key="item.routeName"
            :to="{ name: item.routeName }"
            class="block px-3 py-2 text-sm font-medium text-[#233862] hover:bg-gray-50 rounded"
            :class="{ 'bg-gray-50': isCurrentRoute(item.routeName) }"
          >
            {{ item.name }}
          </router-link>
          <button
            @click="switchLanguage"
            class="block w-full text-left px-3 py-2 text-sm font-medium text-[#233862] hover:bg-gray-50 rounded"
          >
            {{ currentLanguage === 'en' ? 'Ελληνικά' : 'English' }}
          </button>
          <a
            href="https://www.ferryhopper.com/el/?aff_uid=webee"
            target="_blank"
            class="block px-3 py-2 mt-2 text-center bg-[#E65100] text-white rounded text-sm font-medium hover:bg-[#F57C00] transition-colors"
          >
            {{ currentLanguage === 'en' ? 'Book Now' : 'Κράτηση' }}
          </a>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="flex-grow">
      <slot></slot>
    </main>

    <!-- Footer -->
    <footer class="bg-[#233862] text-white py-12">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="flex flex-col h-full">
            <h3 class="text-lg font-semibold mb-4">AKTOPLOIKA</h3>
            <div class="bg-[#1f3157] p-4 rounded-lg h-full flex items-center">
              <p class="text-gray-300 text-sm leading-relaxed">
                {{
                  currentLanguage === 'en'
                    ? 'Your trusted partner for exploring the Greek islands. Fast, safe, and reliable sea transportation.'
                    : 'Ο αξιόπιστος συνεργάτης σας για την εξερεύνηση των ελληνικών νησιών. Γρήγορες, ασφαλείς και αξιόπιστες θαλάσσιες μεταφορές.'
                }}
              </p>
            </div>
          </div>

          <div class="flex flex-col h-full">
            <h3 class="text-lg font-semibold mb-4">
              {{ currentLanguage === 'en' ? 'Important Notice' : 'Συμαντική Ανακοίνωση' }} ⚓
            </h3>
            <div
              class="bg-[#2c4575] p-4 rounded-lg border border-gray-600 h-full flex flex-col justify-center"
            >
              <p class="text-gray-300 text-sm leading-relaxed text-center">
                {{
                  currentLanguage === 'en'
                    ? 'All bookings and ticket handling are managed through our trusted partner Ferryhopper.com. They provide secure payment processing and excellent customer support for all your ferry travel needs.'
                    : 'Οι κρατήσεις και τα εισητήρια διαχειριστέρανται από τον αξιόπιστο συνεργάτη Ferryhopper.com. Παρέχουν ασφαλείς πληρωμές και εξερετική υποστηριξη για τις ταξιοδιοτικές σας ανάγκες.'
                }}
              </p>
            </div>
          </div>

          <div class="flex flex-col h-full">
            <h3 class="text-lg font-semibold mb-4">
              {{ currentLanguage === 'en' ? 'Popular Destinations' : 'Διάσιμοι Προορισμοί' }}
            </h3>
            <div class="bg-[#1f3157] p-4 rounded-lg h-full">
              <ul class="space-y-2">
                <li>
                  <router-link
                    :to="{ name: getRouteName('destination'), params: { id: 'mykonos' } }"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">🏖️</span>
                    {{ currentLanguage === 'el' ? 'Μύκονος' : 'Mykonos' }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="{ name: getRouteName('destination'), params: { id: 'santorini' } }"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">🌅</span>
                    {{ currentLanguage === 'el' ? 'Σαντορίνη' : 'Santorini' }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="{ name: getRouteName('destination'), params: { id: 'crete' } }"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">🏛️</span>
                    {{ currentLanguage === 'el' ? 'Κρήτη' : 'Crete' }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="{ name: getRouteName('destination'), params: { id: 'paros' } }"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">🏝️</span>
                    {{ currentLanguage === 'el' ? 'Πάρος' : 'Paros' }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="{ name: getRouteName('destination'), params: { id: 'corfu' } }"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">🏰</span>
                    {{ currentLanguage === 'el' ? 'Κέρκυρα' : 'Corfu' }}
                  </router-link>
                </li>
              </ul>
            </div>
          </div>

          <div class="flex flex-col h-full">
            <h3 class="text-lg font-semibold mb-4">
              {{ currentLanguage === 'en' ? 'Support' : 'Υποστήριξη' }}
            </h3>
            <div class="bg-[#1f3157] p-4 rounded-lg h-full">
              <p class="text-sm text-gray-300 mb-4">(Ferryhopper)</p>
              <ul class="space-y-3">
                <li>
                  <a
                    href="https://www.ferryhopper.com/en/account/bookings"
                    target="_blank"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">🎫</span>
                    {{ currentLanguage === 'en' ? 'My Bookings' : 'Οι κρατήσεις μου' }}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.ferryhopper.com/en/faq"
                    target="_blank"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">❓</span>
                    {{ currentLanguage === 'en' ? 'FAQ' : 'Συχνες ερωτησεις' }}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.ferryhopper.com/en/contact"
                    target="_blank"
                    class="text-gray-300 hover:text-white text-sm transition-colors flex items-center"
                  >
                    <span class="mr-2">📞</span>
                    {{ currentLanguage === 'en' ? 'Contact Support' : 'Επικοινωνία' }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="mt-8 pt-8 border-t border-gray-700/50 text-center">
          <p class="text-gray-300 text-sm">
            &copy; {{ new Date().getFullYear() }} Aktoploika.eu.
            {{
              currentLanguage === 'en' ? 'All rights reserved.' : 'Με επιφύλαξη παντός δικαιώματος.'
            }}
            <span class="mx-2">|</span>
            <router-link to="/privacy-policy" class="hover:text-white">
              {{ currentLanguage === 'en' ? 'Privacy Policy' : 'Πολιτική Απορρήτου' }}
            </router-link>
          </p>
        </div>
      </div>
    </footer>

    <!-- Cookie Notice -->
    <CookieNotice />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CookieNotice from '@/components/Common/CookieNotice.vue'

const route = useRoute()
const router = useRouter()
const isMobileMenuOpen = ref(false)

// Compute current language from route
const currentLanguage = computed(() => {
  return route.path.startsWith('/el') ? 'el' : 'en'
})

// Function to switch language
const switchLanguage = () => {
  const currentRouteName = route.name || 'homepage'
  const baseRouteName = currentRouteName.replace(/^el/, '')
  const newRouteName = currentLanguage.value === 'en' ? `el${baseRouteName}` : baseRouteName
  
  // Preserve any route params and query
  const routeParams = { ...route.params }
  const routeQuery = { ...route.query }
  
  // Use replace instead of push to avoid browser history issues
  router.replace({ 
    name: newRouteName, 
    params: routeParams,
    query: routeQuery
  })
}

// Compute route names with language prefix
const getRouteName = (baseName) => {
  return currentLanguage.value === 'el' ? `el${baseName}` : baseName
}

// Navigation items with translations
const navItems = computed(() => [
  {
    name: currentLanguage.value === 'el' ? 'Αρχική' : 'Home',
    routeName: getRouteName('homepage'),
  },
  {
    name: currentLanguage.value === 'el' ? 'Προορισμοί' : 'Destinations',
    routeName: getRouteName('destinationsmulti'),
  },
  {
    name: 'Blog',
    routeName: getRouteName('blog'),
  },
])

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const isCurrentRoute = (routeName) => {
  return route.name === routeName
}
</script>
